import React, { useCallback } from "react";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import MenuItem from "@mui/material/MenuItem";
import Grid from "@mui/system/Unstable_Grid/Grid";
import { CircularProgress, Tooltip, Typography } from "@mui/material";
import notificationStore from "../../../../stores/Notification/notificationStore";
import { DebitPermissionDto, PatientBillInfoDto } from "../../../../api-client";
import { transactionMethods, transactionTypes } from "../v2/types/kasse.types";
import { usePatientExistingRxData } from "../../../pages/PatientBills/hooks/usePatientExistingRxData";
import { observer } from "mobx-react";

interface NewTransactionFormProps {
  open: boolean;
  handleTransactionClose: () => void;
  patientId: number;
  rxId: string;
  transactionMethod: transactionMethods;
  debitPermission?: DebitPermissionDto;
  handleCreatePatientTransaction: (
    bill: PatientBillInfoDto,
    transactionMethod: transactionMethods,
    debitPermission?: DebitPermissionDto,
  ) => Promise<void>;
}

const NewTransactionForm: React.FC<NewTransactionFormProps> = observer(
  ({
    open,
    handleTransactionClose,
    patientId,
    rxId,
    transactionMethod,
    debitPermission,
    handleCreatePatientTransaction,
  }) => {
    const { data: billData, isLoading } = usePatientExistingRxData(
      patientId,
      rxId,
    );

    const handleSubmit = useCallback(
      async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!billData) {
          notificationStore.showMessage("Billing data not available", "error");
          return;
        }

        try {
          await handleCreatePatientTransaction(
            billData,
            transactionMethod,
            debitPermission,
          );
          handleTransactionClose();
        } catch (error) {
          console.error("Transaction creation failed:", error);
        }
      },
      [
        billData,
        transactionMethod,
        debitPermission,
        handleCreatePatientTransaction,
        handleTransactionClose,
      ],
    );

    if (isLoading || !billData) {
      return (
        <Dialog open={open} onClose={handleTransactionClose}>
          <DialogContent
            sx={{ display: "flex", justifyContent: "center", p: 4 }}
          >
            <CircularProgress />
          </DialogContent>
        </Dialog>
      );
    }

    const isDisabled = !billData || billData.dueAmount <= 0;

    return (
      <Dialog
        open={open}
        onClose={handleTransactionClose}
        PaperProps={{ component: "form", onSubmit: handleSubmit }}
      >
        <DialogTitle
          sx={{
            fontWeight: "bold",
            fontSize: "h6.fontSize",
            backgroundColor: "white",
            color: "secondary.main",
            textAlign: "center",
          }}
        >
          Neue Transaktion
        </DialogTitle>
        <DialogContent sx={{ my: 1 }}>
          {debitPermission && (
            <Typography variant="h6" sx={{}}>
              <strong>Lastschrift: </strong>
              {debitPermission.firstName} {debitPermission.lastName} (
              {debitPermission.id})
            </Typography>
          )}

          <Typography variant="h6" sx={{}}>
            <strong>Motiv: </strong>
            {billData.tarifGroupId !== "Privat" ? "Co-payment" : "Payment"}
          </Typography>

          <Typography variant="h6" sx={{}}>
            <strong>Mandant: </strong>
            {billData.accountId || "Das Rezept hat keinen Mandanten"}
          </Typography>

          <Typography variant="h6" sx={{ marginBottom: 1 }}>
            <strong>Gesamtpreis: </strong>
            {Math.abs(billData.dueAmount).toFixed(2).replace(".", ",")}€
            {billData.isProvisionalPrice
              ? " (Geschätzt - Noch sind nicht alle Termine festgelegt)"
              : ""}
          </Typography>

          <DialogContentText sx={{ marginBottom: 1 }}>
            Bitte füllen Sie das Formular zur Erstellung einer Transaktion
            Buchung sorgfältig aus.
          </DialogContentText>

          <Grid container spacing={2}>
            <Grid>
              <TextField
                id="transaction-type"
                select
                label="Typ"
                name="transactionType"
                defaultValue={transactionTypes.EINNAHME}
                variant="filled"
                helperText="Bitte geben Sie den Transaktionstyp an"
                fullWidth
              >
                <MenuItem value={transactionTypes.EINNAHME}>
                  {transactionTypes.EINNAHME}
                </MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleTransactionClose}>Abbrechen</Button>
          <Tooltip
            title={
              isDisabled
                ? "Die vollständige Zahlung für dieses Rezept wurde geleistet"
                : ""
            }
            arrow
          >
            <span>
              <Button
                type="submit"
                disabled={isDisabled}
                style={{ pointerEvents: isDisabled ? "none" : "auto" }}
              >
                Einreichen
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      </Dialog>
    );
  },
);

export default NewTransactionForm;
